import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import { ColumnDef } from '@tanstack/react-table';
import { TanTable } from '../../components/TanTable';
import {
  useAdditionalFieldSelection,
  useAdditionalFieldColumns,
} from '../../hooks/useAdditionalFields';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import { useVisibilityAwareInterval } from '../../hooks/useVisibilityAwareInterval';
import api from '../../services/api';
import { config } from '../../config/config';

// Live report — re-pull the data every 60s; the elapsed clocks tick locally.
const AUTO_REFRESH_MS = 60000;

// Detention thresholds (ms since check-in): warning at 1:00, detention at 1:30.
const WARN_MS = 60 * 60 * 1000;
const DETENTION_MS = 90 * 60 * 1000;
// Completed loads: flag red when the total dwell ran over 2:00.
const COMPLETED_RED_MS = 2 * 60 * 60 * 1000;

interface DetentionRow {
  pronumber: string;
  movetype: string;
  carrier: string;
  customer: string;
  lc: string;
  whse: string;
  delivery_appt: string | null;
  checkin_time: string | null;
  completed_time: string | null;
  revenue: number | null;
  total_expense: number | null;
  carrier_expense: number | null;
  profit: number | null;
  margin: number | null;
}

const toYMD = (d: Date): string =>
  `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

// Date-only value (YYYY-MM-DD or the date portion of an ISO datetime) as M/D/YY,
// no timezone shift.
const formatDate = (dateStr: string | null): string => {
  if (!dateStr || dateStr.startsWith('0000-00-00')) return '-';
  const [y, m, d] = dateStr.slice(0, 10).split('-');
  if (y && m && d) return `${Number(m)}/${Number(d)}/${y.slice(2)}`;
  return dateStr;
};

// ISO (no timezone) datetime -> "M/D/YY h:mm AM" in local time.
const formatDateTime = (s: string | null): string => {
  if (!s || s.startsWith('0000')) return '-';
  const d = new Date(s);
  if (isNaN(d.getTime())) return s;
  return d.toLocaleString('en-US', {
    month: 'numeric', day: 'numeric', year: '2-digit', hour: 'numeric', minute: '2-digit',
  });
};

const formatCurrency = (value: number | null): string =>
  value == null ? '-' : value.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

// dashboard_cst.margin is stored as a percent value.
const formatPercent = (value: number | null): string =>
  value == null ? '-' : `${value.toFixed(1)}%`;

// Sum a numeric column across the given (filtered) rows for the subtotal footer.
const sumBy = (rows: { original: DetentionRow }[], key: keyof DetentionRow): number =>
  rows.reduce((acc, r) => acc + (Number(r.original[key]) || 0), 0);

// Duration ms -> "H:MM:SS", prefixed with "Dd " past a day.
const formatDuration = (ms: number): string => {
  const total = Math.max(0, Math.floor(ms / 1000));
  const s = total % 60;
  const m = Math.floor(total / 60) % 60;
  const h = Math.floor(total / 3600) % 24;
  const d = Math.floor(total / 86400);
  const hms = `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return d > 0 ? `${d}d ${hms}` : hms;
};

const detentionColor = (ms: number): string =>
  ms >= DETENTION_MS ? '#ef4444' : ms >= WARN_MS ? '#f59e0b' : '#10b981';

// A completion stamped before the check-in is bad data — render "n/a" for it.
const isBadCompletion = (checkin: string | null, completed: string | null): boolean =>
  !!checkin && !!completed && new Date(completed).getTime() < new Date(checkin).getTime();

// Elapsed time since check-in. Ticks every second while the load is still open
// (no completed time); once completed it shows the static total dwell.
function ElapsedClock({ checkin, completed }: { checkin: string | null; completed: string | null }) {
  const live = !!checkin && !completed;
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!live) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [live]);

  if (!checkin) return <span className="text-gray-400">-</span>;
  // Completion before check-in is invalid — show n/a (the load falls off the
  // still-open board via the backend, since it counts as completed).
  if (isBadCompletion(checkin, completed)) return <span className="text-gray-400">n/a</span>;
  const start = new Date(checkin).getTime();
  const end = completed ? new Date(completed).getTime() : now;
  const ms = Math.max(0, end - start);
  // Open loads follow the live detention bands (green/amber/red). Completed loads
  // show their total dwell in neutral, but turn red if it ran over 2:00.
  let color: string | undefined;
  let bold: boolean;
  if (completed) {
    const over = ms >= COMPLETED_RED_MS;
    color = over ? '#ef4444' : undefined;
    bold = over;
  } else {
    color = detentionColor(ms);
    bold = ms >= WARN_MS;
  }
  const neutral = completed && !color ? 'text-muted' : '';
  return (
    <span className={`tabular-nums ${bold ? 'font-semibold' : ''} ${neutral}`}
          style={color ? { color } : undefined}>
      {formatDuration(ms)}
    </span>
  );
}

function CSTDetentionRiskContent() {
  // Any orders-scope registry field can be added as a column here; the backend
  // attaches it to the rows this report already returned, keyed on pronumber.
  const fieldSelection = useAdditionalFieldSelection('cst-detention-risk');
  const additionalFieldsKey = fieldSelection.additionalFields.join(',');
  const fieldsReady = fieldSelection.ready;

  const [data, setData] = useState<DetentionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isMounted = useRef(true);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Check-in date range. Both default to today; capped at today (check-ins are in
  // the past), floored a year back.
  const { todayStr, minStr } = useMemo(() => {
    const today = new Date();
    const min = new Date();
    min.setFullYear(min.getFullYear() - 1);
    return { todayStr: toYMD(today), minStr: toYMD(min) };
  }, []);
  const [startDate, setStartDate] = useState(todayStr);
  const [endDate, setEndDate] = useState(todayStr);

  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  const [isOrderDrawerOpen, setIsOrderDrawerOpen] = useState(false);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      abortControllerRef.current?.abort();
    };
  }, []);

  const fetchData = useCallback(async (mode: 'initial' | 'background' = 'background') => {
    if (!isMounted.current) return;
    const initial = mode === 'initial';
    if (abortControllerRef.current) abortControllerRef.current.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    try {
      if (initial) {
        setError(null);
        setData([]);
        setLoading(true);
      }
      // The saved selection is part of the request; going early just fetches twice.
      if (!fieldsReady) return;
      const response = await api.get('/reporting/cst-detention-risk', {
        params: {
          start_date: startDate,
          end_date: endDate,
          // A timer-driven refresh is the page keeping itself current, not somebody
          // asking for the report, so it is not recorded as a run.
          ...(initial ? {} : { background: true }),
          ...(additionalFieldsKey ? { additional_fields: additionalFieldsKey } : {}),
        },
        signal: controller.signal,
      });
      if (!isMounted.current || controller.signal.aborted) return;
      setData(response.data);
      setError(null);
    } catch (err) {
      if (!isMounted.current || controller.signal.aborted) return;
      if (initial) setError(err instanceof Error ? err.message : 'Failed to fetch data');
    } finally {
      if (isMounted.current && !controller.signal.aborted && initial) setLoading(false);
    }
  }, [startDate, endDate, fieldsReady, additionalFieldsKey]);

  useEffect(() => {
    fetchData('initial');
  }, [fetchData]);

  useVisibilityAwareInterval(() => {
    fetchData('background');
  }, AUTO_REFRESH_MS);

  const openOrderDrawer = useCallback((pronumber: number) => {
    setSelectedPro(pronumber);
    setIsOrderDrawerOpen(true);
  }, []);
  const closeOrderDrawer = useCallback(() => {
    setIsOrderDrawerOpen(false);
    setSelectedPro(null);
  }, []);

  const renderProCell = useCallback((pronumber: string) => {
    if (!pronumber) return <span className="text-gray-400">-</span>;
    // Viewed/last-viewed highlight + click-to-mark is applied by TanTable's
    // trackViewedColumnId="pronumber" wrapper.
    return (
      <div className="flex items-center gap-1">
        <button
          onClick={(e) => {
            e.stopPropagation();
            openOrderDrawer(Number(pronumber));
          }}
          className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 hover:underline"
        >
          {pronumber}
        </button>
        <a
          href={`${config.LEGACY_CMS_URL}/index_cms.pl?action=savecustomsearch&contlist=${pronumber}&searchtype=pronumber`}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => { e.stopPropagation(); }}
          className="text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300"
          title="Open in CMSV1"
        >
          <ArrowTopRightOnSquareIcon className="h-4 w-4" />
        </a>
      </div>
    );
  }, [openOrderDrawer]);

  const baseColumns = useMemo<ColumnDef<DetentionRow>[]>(() => [
    {
      accessorKey: 'pronumber',
      header: 'Pronumber',
      filterFn: 'arrIncludes',
      cell: ({ row }) => renderProCell(row.original.pronumber),
      footer: () => <span className="font-semibold">Totals</span>,
    },
    {
      accessorKey: 'movetype',
      header: 'Movetype',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'carrier',
      header: 'Carrier',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'customer',
      header: 'Customer',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'lc',
      header: 'LC',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'whse',
      header: 'WHSE',
      filterFn: 'arrIncludes',
      cell: (info) => info.getValue<string>() || '-',
    },
    {
      accessorKey: 'delivery_appt',
      header: 'Delivery Appt',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDate(row.original.delivery_appt),
    },
    {
      accessorKey: 'checkin_time',
      header: 'Check-In',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatDateTime(row.original.checkin_time),
    },
    {
      id: 'elapsed',
      header: 'Elapsed',
      enableColumnFilter: false,
      cell: ({ row }) => (
        <ElapsedClock checkin={row.original.checkin_time} completed={row.original.completed_time} />
      ),
    },
    {
      accessorKey: 'completed_time',
      header: 'Completed',
      filterFn: 'arrIncludes',
      cell: ({ row }) => {
        const { checkin_time, completed_time } = row.original;
        if (!completed_time) return <span className="text-gray-400">-</span>;
        if (isBadCompletion(checkin_time, completed_time)) return <span className="text-gray-400">n/a</span>;
        return formatDateTime(completed_time);
      },
    },
    {
      accessorKey: 'revenue',
      header: 'Revenue',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.revenue),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'revenue'))}
        </span>
      ),
    },
    {
      accessorKey: 'total_expense',
      header: 'Total Expense',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.total_expense),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'total_expense'))}
        </span>
      ),
    },
    {
      accessorKey: 'carrier_expense',
      header: 'Carrier Expense',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.carrier_expense),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'carrier_expense'))}
        </span>
      ),
    },
    {
      accessorKey: 'profit',
      header: 'Profit',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatCurrency(row.original.profit),
      footer: ({ table }) => (
        <span className="font-semibold">
          {formatCurrency(sumBy(table.getFilteredRowModel().rows, 'profit'))}
        </span>
      ),
    },
    {
      accessorKey: 'margin',
      header: 'Margin',
      filterFn: 'arrIncludes',
      cell: ({ row }) => formatPercent(row.original.margin),
      // Blended margin across the filtered rows: total profit / total revenue.
      footer: ({ table }) => {
        const rows = table.getFilteredRowModel().rows;
        const revenue = sumBy(rows, 'revenue');
        return (
          <span className="font-semibold">
            {revenue !== 0 ? formatPercent((sumBy(rows, 'profit') / revenue) * 100) : '-'}
          </span>
        );
      },
    },
  ], [renderProCell]);

  // Rows are report rows, not orders — several can share a pro — so an added order
  // column repeats down them rather than blanking.
  const { columns, availableFields } = useAdditionalFieldColumns<DetentionRow>(
    fieldSelection, { baseColumns },
  );

  const getRowId = useCallback((row: DetentionRow) => row.pronumber, []);

  const infoBanner = (
    <div className="px-4 py-2 text-sm text-muted bg-gray-50 dark:bg-gray-800/50 border-b border-gray-200 dark:border-gray-700">
      CST loads that checked in at a warehouse, with a live elapsed clock since check-in. Elapsed turns
      <span className="text-amber-600 dark:text-amber-400 font-medium"> amber at 1:00</span> and
      <span className="text-red-600 dark:text-red-400 font-medium"> red at 1:30</span> (detention). Completed
      loads show their total dwell, red if it ran over 2:00. Shows the date range's check-ins plus any
      still-open load that checked in earlier and hasn't completed yet (both default today); auto-refreshes
      every 60 seconds.
    </div>
  );

  if (error) {
    return (
      <div className="flex flex-col h-full">
        {infoBanner}
        <div className="flex-1 flex items-center justify-center"><div className="text-red-500">{error}</div></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {infoBanner}

      <div className="flex items-center gap-4 px-4 py-2 border-b border-gray-200 dark:border-gray-700 flex-wrap">
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          From
          <input
            type="date"
            value={startDate}
            min={minStr}
            max={endDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-panel text-body"
          />
        </label>
        <label className="flex items-center gap-1.5 text-sm text-gray-600 dark:text-gray-300">
          To
          <input
            type="date"
            value={endDate}
            min={startDate}
            max={todayStr}
            onChange={(e) => setEndDate(e.target.value)}
            className="border border-gray-300 dark:border-gray-600 rounded px-2 py-1 text-sm bg-panel text-body"
          />
        </label>
        <span className="text-xs text-gray-400 dark:text-gray-500">check-in date · plus any still-open loads from earlier</span>
      </div>

      <div className="flex-1 min-h-0 p-4">
        <TanTable
          data={data}
          columns={columns}
          availableFields={availableFields}
          additionalFields={fieldSelection.additionalFields}
          onAdditionalFieldsChange={fieldSelection.onAdditionalFieldsChange}
          trackViewedColumnId="pronumber"
          loading={loading}
          defaultSorting={[{ id: 'checkin_time', desc: false }]}
          settingsKey="cst-detention-risk"
          enableColumnFilters={true}
          enableGrouping={true}
          getRowId={getRowId}
          cardTitle="CST Detention Risk"
          fillContainer={true}
          showColumnFooter={true}
        />
      </div>

      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={isOrderDrawerOpen && selectedPro !== null}
        onClose={closeOrderDrawer}
      />
    </div>
  );
}

function CSTDetentionRisk() {
  return (
    <ViewedProsProvider>
      <CSTDetentionRiskContent />
    </ViewedProsProvider>
  );
}

export default CSTDetentionRisk;
